import { Ioc } from "@core/ioc/ioc";
import type { IStorageModel } from "./IStorageModel";
import { STORAGE_MODEL } from "./IStorageModel";

/**
 * `thumbnailCache.ts` — module-scoped cache shared by every
 * thumbnail consumer (`useProjectThumbnail`, `useSceneThumbnail`, and
 * any future asset-library / inspector preview).
 *
 * Mirrors Unity's `IProjectThumbnailsCache` semantics:
 *   - cache key = `customerId|projectId|thumbnailId|modifiedAt`
 *     (`IsThumbnailCacheValid` keys on the resolved thumbnail id and
 *     invalidates whenever the source's `ModifiedTimestamp` advances)
 *   - cached `blob:` URLs intentionally outlive consumer scope —
 *     re-mounting the projects grid should not refetch S3, and Vue's
 *     `URL.revokeObjectURL` cleanup would force exactly that.
 *   - 404 from S3 returns `null`; callers fall back to the legacy
 *     raw-`sceneId` key (`ProjectThumbnailsCache.cs:515-525`).
 */

interface CacheEntry {
  readonly url: string;
  readonly modifiedAt: string;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<string | null>>();

export interface ThumbnailRequest {
  readonly customerId: string;
  readonly projectId: string;
  /** Fully-resolved id (post-`generateThumbnailId`). */
  readonly thumbnailId: string;
  /**
   * Optional fallback id used when the primary fetch 404s. Mirrors
   * Unity's "try thumbnailID first, then raw sceneID" path. Pass the
   * raw `sceneId` here when `thumbnailId` was instance-encoded; pass
   * `undefined` when there's nothing to fall back to.
   */
  readonly fallbackThumbnailId?: string;
  /**
   * Cache-invalidation timestamp — typically `SceneData.ModifiedTimestamp`
   * for scene-level thumbnails or `ProjectData.ModifiedTimestamp` for
   * project-level previews.
   */
  readonly modifiedAt: string;
}

/**
 * Fetch (or hit the cache for) a single thumbnail. Returns the
 * `blob:` URL or `null` when the object can't be retrieved. Safe to
 * call concurrently for the same key — duplicate inflight requests
 * are de-duplicated.
 */
export async function loadThumbnail(
  req: ThumbnailRequest,
  signal?: AbortSignal,
): Promise<string | null> {
  const { customerId, projectId, thumbnailId, fallbackThumbnailId, modifiedAt } =
    req;
  if (!customerId || !projectId || !thumbnailId) return null;

  const key = `${customerId}|${projectId}|${thumbnailId}|${modifiedAt}`;
  const cached = cache.get(key);
  if (cached) return cached.url;

  const existing = inflight.get(key);
  if (existing) return existing;

  const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);

  const promise = (async () => {
    let url = await storage.getProjectThumbnailBlobUrl(
      customerId,
      projectId,
      thumbnailId,
      signal,
    );
    if (
      !url &&
      fallbackThumbnailId &&
      fallbackThumbnailId !== thumbnailId
    ) {
      url = await storage.getProjectThumbnailBlobUrl(
        customerId,
        projectId,
        fallbackThumbnailId,
        signal,
      );
    }
    if (url) cache.set(key, { url, modifiedAt });
    return url;
  })();

  inflight.set(key, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(key);
  }
}

/**
 * Drop every cached entry for a given project. Call when a project is
 * deleted / re-imported / its `ModifiedTimestamp` changes globally.
 * Mirrors `IProjectThumbnailsCache.ClearThumbnailsForProject`.
 */
export function clearProjectThumbnails(projectId: string): void {
  for (const key of [...cache.keys()]) {
    const parts = key.split("|");
    if (parts[1] === projectId) {
      const entry = cache.get(key);
      if (entry) URL.revokeObjectURL(entry.url);
      cache.delete(key);
    }
  }
}

/**
 * Drop EVERY cached thumbnail and revoke its `blob:` URL. Backs the app
 * settings "Clear Cache" action (`AppSettingsViewModel.ClearCache` →
 * `IAppCache.ClearCacheAsync`). In-flight loads are intentionally left alone:
 * they resolve into the now-empty cache and simply re-populate it, which is
 * the same outcome as the request arriving a moment later.
 */
export function clearAllThumbnails(): number {
  const count = cache.size;
  for (const entry of cache.values()) URL.revokeObjectURL(entry.url);
  cache.clear();
  return count;
}
